import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../../../environments/environment';

/** Forma cruda de GET /formularios/vigente — SchemaJson viaja como string JSON. */
export interface FormularioVigenteResponse {
  id_formulario: string;
  cod_sociedad: string;
  version: string;
  SchemaJson: string;
  fecha_creacion: string;
  /**
   * 15-sep-2026, a pedido explícito (Roberto): eco de los 5 datos del cliente que se mandaron
   * por query (ver ConsentimientoApiService.getFormularioVigente) — null si no se mandó ninguno.
   */
  Cliente: {
    rut: string | null;
    nombres: string | null;
    apellidos: string | null;
    email: string | null;
    telefono: string | null;
  } | null;
}

/** Los mismos 5 datos que trae GET /formularios/vigente en Cliente — se mandan opcionalmente
 * para que el backend los devuelva junto con el formulario (ver WidgetConsentimientoComponent). */
export interface DatosClienteConocidos {
  rut?: string;
  nombres?: string;
  apellidos?: string;
  email?: string;
  telefono?: string;
}

/**
 * ESP-FORM-004 (28-ago-2026, a pedido explícito): cod_sociedad ya no va en este body — el
 * backend la deduce de id_formulario. Cambio acotado a este servicio (POST /consentimientos);
 * GET /formularios/vigente sigue recibiendo cod_sociedad como query param, sin cambios.
 */
export interface ConsentimientoItemPayload {
  clave_idempotencia: string;
  rut: string;
  cliente?: { nombres?: string; apellidos?: string; telefono?: string; email?: string };
  aplica_al_grupo: boolean;
  id_formulario: string;
  /** A pedido explícito (25-sep-2026): de dónde vino el acto -- 'web' siempre, porque este
   * widget solo vive en la web. */
  medio?: string;
  /** A pedido explícito (25-sep-2026): qué página lo invocó (ver ConsentimientoMarcaComponent
   * / WidgetConsentimientoComponent.origen) -- depende de la página, no del formulario. */
  origen?: string;
  finalidades: { codigo: string; otorgado: boolean }[];
}

export interface ConsentimientoResultadoItem {
  resultado: 'CREADO' | 'YA_REGISTRADO' | 'ERROR';
  id_consentimiento: string | null;
  codigo_error: string | null;
  mensaje: string | null;
}

export interface ConsentimientoArregloResponse {
  id_llamada: string;
  resumen: { total: number; exitosos: number; con_error: number };
  resultados: ConsentimientoResultadoItem[];
}

/**
 * Cliente HTTP hacia widget-consentimiento-backend. Todavía no maneja
 * reintentos, refresh de token ni loading state — eso queda para cuando el
 * widget se integre a una página real (ver plan de Fase 1/2).
 *
 * CORS: el backend ya tiene `app.enableCors()` en `main.ts` (verificado
 * 07-sep-2026, admite cualquier origen) — el comentario anterior acá decía
 * lo contrario porque quedó desactualizado desde ago-2026, cuando todavía
 * no estaba habilitado.
 */
@Injectable({ providedIn: 'root' })
export class ConsentimientoApiService {
  private readonly baseUrl = environment.apiUrl;
  private readonly http = inject(HttpClient);

  // Nombres de query params con guión bajo (28-ago-2026, a pedido explícito): el backend
  // ahora espera cod_sociedad/cod_tipo_formulario, no codSociedad/codTipoFormulario — los
  // nombres de los argumentos acá adentro (en TS) no cambian, solo las claves que viajan
  // en la URL. codTipoFormulario ahora es obligatorio del lado del backend si se manda
  // cod_sociedad (ver WidgetConsentimientoComponent, que ya lo manda siempre con default 'CONSENT').
  getFormularioVigente(
    codSociedad: string,
    codTipoFormulario?: string,
    datosCliente?: DatosClienteConocidos,
  ): Observable<FormularioVigenteResponse> {
    const params: Record<string, string> = { cod_sociedad: codSociedad };
    if (codTipoFormulario) {
      params['cod_tipo_formulario'] = codTipoFormulario;
    }
    // 15-sep-2026, a pedido explícito: si la página host ya conoce al cliente, se los pasamos
    // al backend — vuelven en Cliente (ver FormularioVigenteResponse) y sirven para
    // autocompletar el formulario (ver WidgetConsentimientoComponent).
    if (datosCliente?.rut) params['rut'] = datosCliente.rut;
    if (datosCliente?.nombres) params['nombres'] = datosCliente.nombres;
    if (datosCliente?.apellidos) params['apellidos'] = datosCliente.apellidos;
    if (datosCliente?.email) params['email'] = datosCliente.email;
    if (datosCliente?.telefono) params['telefono'] = datosCliente.telefono;
    return this.http.get<FormularioVigenteResponse>(`${this.baseUrl}/formularios/vigente`, { params });
  }

  postConsentimientos(items: ConsentimientoItemPayload[]): Observable<ConsentimientoArregloResponse> {
    const headers = new HttpHeaders({ traceparent: this.generarTraceparent() });
    return this.http.post<ConsentimientoArregloResponse>(`${this.baseUrl}/consentimientos`, items, {
      headers,
    });
  }

  /** Header traceparent (formato W3C) que exige TraceparentGuard en el backend
   * — 00-{32 hex}-{16 hex}-{2 hex}. El backend rechaza el request si falta. */
  private generarTraceparent(): string {
    const hex = (len: number) =>
      Array.from({ length: len }, () => Math.floor(Math.random() * 16).toString(16)).join('');
    return `00-${hex(32)}-${hex(16)}-01`;
  }
}
